import React, { memo, useCallback, useState } from "react";
import "./MedicalEscort.css";
import { TextareaAutosize } from "@material-ui/core";
import {
  MedicalEscortDetails,
  Visit,
  VisitSchedule,
} from "../../@types/PricingModels";
import { c4A4A4A, c7C7C7C, UNDEF } from "../../Constants";
import {
  is_medical_appointment,
  medical_appointment_time,
  medical_appointment_location,
  medical_appointment_detail,
  is_providing_transportation,
  transportation_details,
  name,
  phone,
  visit_schedule,
  medical_escort_details,
  visit_location_address,
} from "../../DataConstants";
import { Div, Txt } from "../GenericView";
import { TimePicker } from "../CommonComponents";
import {
  convertToDestTZ,
  dateToAMPM,
  fifteenMinIntervalTimeEntries,
  parseDate,
} from "../../Util/DateUtils";
import { getLocationText } from "../LocationInput";
import { guessMinutes, isBlank } from "Util/Utilities";

/**
 * Note: This component directly updates the `medEscortDetails` passed through props
 */

interface MEProps {
  region: string | undefined;
  /**
   * Visit start date/time
   */
  startDate?: Date;

  /**
   * Visit end date/time
   */
  endDate?: Date;

  /**
   * Medical escort details object
   * Note: component will update this directly
   */
  medEscortDetails: MedicalEscortDetails;

  /**
   * Update callback
   * Callback will be invoked after updating the medEscortDetails object
   */
  onUpdate: (details: MedicalEscortDetails) => void;
}

const MedicalEscort: React.FC<MEProps> = (props) => {
  //To force re-render this component
  const [rc, render] = useState(1);

  const { medEscortDetails, onUpdate } = props;

  const contactName = medEscortDetails[name] || "";
  const contactPhone = medEscortDetails[phone] || "";
  const isMedicalAppt = medEscortDetails[is_medical_appointment];

  const apptLocation =
    isMedicalAppt && medEscortDetails[medical_appointment_location];
  const time = isMedicalAppt
    ? medEscortDetails[medical_appointment_time]
    : UNDEF;

  // when it's not a medical appointment
  const apptDetails =
    (!isMedicalAppt && medEscortDetails[medical_appointment_detail]) || "";

  const isProvidingTransport = medEscortDetails[is_providing_transportation];
  const transportationDetails =
    (isProvidingTransport && medEscortDetails[transportation_details]) || "";

  const apptTimeError = getAppointmentTimeError(
    props.startDate,
    props.endDate,
    time,
    props.region
  );

  // To trigger render
  const reRender = useCallback(() => render(rc + 1), [rc]);

  const onTextChange = useCallback(
    (
      type:
        | "name"
        | "phone"
        | "medical_appointment_location"
        | "medical_appointment_detail"
        | "transportation_details"
    ) =>
      (e: React.ChangeEvent<any>) => {
        medEscortDetails[type] = e.target.value;
        onUpdate(medEscortDetails);
        reRender();
      },
    [medEscortDetails, onUpdate, reRender]
  );

  const onTimeChange = useCallback(
    (time: string) => {
      medEscortDetails[medical_appointment_time] = time;
      onUpdate(medEscortDetails);
      reRender();
    },
    [medEscortDetails, onUpdate, reRender]
  );

  const yesNoChange = useCallback(
    (
      type: "is_medical_appointment" | "is_providing_transportation",
      isYes: boolean
    ) =>
      () => {
        medEscortDetails[type] = isYes;
        onUpdate(medEscortDetails);
        reRender();
      },
    [medEscortDetails, onUpdate, reRender]
  );

  return (
    <Div v color={c4A4A4A} mt={22} ml={2} fs={14} width={416}>
      <Txt m>Who should the Care Pro contact?</Txt>
      <Div h g mt={12}>
        <input
          placeholder="Name"
          value={contactName}
          // error={nameError}
          className="med-inp"
          onChange={onTextChange(name)}
        />
        <Div width={10} />

        <input
          type="tel"
          placeholder="Phone No."
          value={contactPhone}
          // error={phoneNumberError}
          className="med-inp"
          onChange={onTextChange(phone)}
        />
      </Div>

      <Div mt={30}>
        <Txt m>Is this for a medical appointment?</Txt>
        <Div className="flex" mt={12}>
          <Txt
            className="med-chip"
            isSelected={isMedicalAppt}
            onClick={yesNoChange(is_medical_appointment, true)}
            mr={12}
          >
            Yes
          </Txt>
          <Txt
            className="med-chip"
            isSelected={isMedicalAppt === false}
            onClick={yesNoChange(is_medical_appointment, false)}
          >
            No
          </Txt>
        </Div>
      </Div>

      {isMedicalAppt && (
        <>
          <Div mt={16} h g>
            <input
              placeholder="Appointment location"
              value={apptLocation || ""}
              // error={apptLocError}
              className="med-inp"
              onChange={onTextChange(medical_appointment_location)}
            />

            <TimePicker
              placeholder="Time"
              className="med-tp"
              width="103px"
              ampmOutput
              hideFocusBar
              showDropdownArrow
              dontDisableDocScroll
              timeEntries={fifteenMinIntervalTimeEntries}
              selectedItem={time}
              onChange={onTimeChange}
            />
          </Div>
          {apptTimeError && (
            <Txt color="#ec0808" fs={12} mt={5} textAlign="right">
              {apptTimeError}
            </Txt>
          )}
        </>
      )}

      {isMedicalAppt === false && (
        <Div mt={16} v>
          <Txt color={c7C7C7C} fs={12}>
            Please describe what the medical escort is for
          </Txt>

          <Div mt={8} mr={3}>
            <TextareaAutosize
              className="w-full med-textarea"
              minRows={3}
              placeholder="e.g. I need help to transport my mother from her house to my house"
              value={apptDetails}
              onChange={onTextChange(medical_appointment_detail)}
            />
          </Div>
        </Div>
      )}

      <Div mt={30}>
        <Txt m>Will the customer be providing the transportation?</Txt>
        <Div className="flex" mt={12}>
          <Txt
            className="med-chip"
            isSelected={isProvidingTransport}
            onClick={yesNoChange(is_providing_transportation, true)}
            mr={12}
          >
            Yes
          </Txt>
          <Txt
            onClick={yesNoChange(is_providing_transportation, false)}
            isSelected={isProvidingTransport === false}
            className="med-chip"
          >
            No
          </Txt>
        </Div>
      </Div>
      {isProvidingTransport && (
        <Div mt={21} mr={3}>
          <TextareaAutosize
            className="w-full med-textarea"
            minRows={3}
            placeholder="Transportation Details"
            value={transportationDetails}
            onChange={onTextChange(transportation_details)}
          />
        </Div>
      )}
    </Div>
  );
};

export default memo(MedicalEscort);

export const getMedicalEscortDetails = (visit: Visit, region: any) => {
  const schedule = visit[visit_schedule];
  const startDateObj = visit.startDate && parseDate(visit.startDate as any);
  const startDate = startDateObj || (schedule && parseDate(schedule.date_from));
  const details = visit[medical_escort_details];
  const location = visit[visit_location_address];

  if (!startDate || !details || !location) return UNDEF;

  const dateTxt = dateToAMPM(convertToDestTZ(startDate, region), false);

  const reportingTime = dateTxt ? " at " + dateTxt : ""; // 3:00 am
  let reportLocation = !location ? "" : " at " + getLocationText(location);

  const name = details.name || "";
  const phone = details.phone || "";
  const time =
    (details[is_medical_appointment] && details[medical_appointment_time]) ||
    "";

  const apptLocation =
    (details[is_medical_appointment] &&
      details[medical_appointment_location]) ||
    "";

  const nameAndPhone =
    name || phone
      ? "to " + (name || "") + (phone ? " (" + phone + ")" : "")
      : "";
  const reportTo = `- Report${reportingTime}${reportLocation} ${nameAndPhone}\n`;

  const isMedAppt = details[is_medical_appointment];
  const appointment = !(isMedAppt || isMedAppt === false)
    ? ""
    : "- " +
    (!details[is_medical_appointment]
      ? "Medical Escort Description: " +
      (details[medical_appointment_detail] || "")
      : "Appointment: " + (time ? time + ". " : "") + apptLocation) +
    "\n";

  const providingTransport = details[is_providing_transportation];
  const transportationDetail = providingTransport
    ? details[transportation_details] || ""
    : "No";
  const transport =
    providingTransport === UNDEF
      ? ""
      : "- Transportation: " + transportationDetail;

  return reportTo + appointment + transport;
};

/**
 *
 * @param visitSchedule schedule from visit
 * @param details Medical escort details
 * @return error message or undefined
 */
export function getMedicalEscortError(
  visitSchedule?: VisitSchedule,
  details?: MedicalEscortDetails,
  region?: any
): string | undefined {
  const errorMissingDetails = "Please specify medical escort details";

  if (!details || !visitSchedule) {
    return errorMissingDetails;
  }
  const startDate = parseDate(visitSchedule.date_from);
  const endDate = parseDate(visitSchedule.date_to);

  const contactName = details[name];
  const contactPhone = details[phone];
  const isMedicalAppt = details[is_medical_appointment];
  const medApptDetail = details[medical_appointment_detail];
  const medApptLocation = details[medical_appointment_location];
  const medApptTime = details[medical_appointment_time] || "";

  const providingTransport = details[is_providing_transportation];
  const transportationDetail = details[transportation_details];

  if (
    !startDate ||
    !endDate ||
    !contactName ||
    !contactPhone ||
    isMedicalAppt === UNDEF ||
    providingTransport === UNDEF
  ) {
    return errorMissingDetails;
  }

  if (
    (isMedicalAppt === false && isBlank(medApptDetail)) ||
    (isMedicalAppt && (isBlank(medApptLocation) || isBlank(medApptTime))) ||
    (providingTransport && isBlank(transportationDetail))
  ) {
    return errorMissingDetails;
  }

  // Validate appointment time
  return getAppointmentTimeError(startDate, endDate, medApptTime, region);
}

export function getAppointmentTimeError(
  startDate?: Date,
  endDate?: Date,
  apptTime?: string,
  region?: any
) {
  const apptMins = guessMinutes(apptTime || "");

  if (apptMins !== UNDEF && startDate && endDate) {
    const startMins = startDate.getHours() * 60 + startDate.getMinutes();
    const endMins = endDate.getHours() * 60 + endDate.getMinutes();
    const nextDay = endMins <= startMins;

    if (
      !nextDay
        ? apptMins < startMins || apptMins >= endMins
        : !(apptMins >= startMins && apptMins > endMins) &&
        !(apptMins <= startMins && apptMins < endMins)
    ) {
      return (
        "Appointment time should be within visit hours " +
        `(${dateToAMPM(convertToDestTZ(startDate, region))} to ${dateToAMPM(
          convertToDestTZ(endDate, region)
        )})`
      );
    }
  }
}
